<template>
  <Card
    class="dropdown-spacing"
    :footerDivider="false"
    :loadingState="false"
    :title="$t('termination.headline')"
  >
    <template v-slot:content>
      <div class="card-row">
        <div class="card-row__col">
          <p v-dompurify-html="$t('termination.subtext')"></p>
          <h2>{{ $t('termination.dropDown.header') }}</h2>
          <div id="select-div">
            <SelectMenue
              v-if="reasonOptions.length > 0"
              :label="$t('termination.dropDown.placeholder')"
              :options="reasonOptions"
              :placeholder="$t('termination.dropDown.placeholder')"
              idProp="termination-reason"
              @update:input="handleReasonChanged"
            >
            </SelectMenue>
          </div>
          <Alert v-if="actionText != ''" class="mt-47" :text="actionText"> </Alert>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="goBack"
          class="padding-all-0"
          :buttonColor="ButtonColor.LINK"
          >{{ $t('termination.zurueck') }}</CustomButton
        >
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { onMounted, type Ref, ref } from 'vue'
import { Alert, ButtonColor, Card, CustomButton, SelectMenue } from 'de-zurich-ui-library'
import { t } from '@/i18n'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import { removeDuplicateSlashesInString } from '@/utils/CommonUtils'
import { logTerminationReason } from '@/stores/motorService'

const props = defineProps({
  policyId: {
    type: String,
    required: true
  }
})

// Interfaces
interface Option {
  label: string
  value: string
}

// Variables
const actionText = ref('')
const partnerProxyId: Ref<string | null> = ref(null)
const reasonOptions: Ref<Option[]> = ref([])
const reasonValues: string[] = [
  'newCar',
  'holderChange',
  'newAddress',
  'serviceScope',
  'contributionAmount',
  'changeInsurance',
  'terminateAnyway'
]

// Life Cycle Hooks
onMounted(() => {
  // fill Options array with reasonValues and localized label
  for (const reason of reasonValues) {
    reasonOptions.value.push({
      label: t('termination.reason.' + reason) as string,
      value: reason
    })
  }
})

// Functions
function goBack(): void {
  globalThis.history.back()
}

function getRoute(): string {
  let route: string = globalThis.location.href
  if (route.endsWith('/')) {
    route = route.substring(0, route.length - 1)
  }

  const pos = route.lastIndexOf('/')
  route = route.substring(0, pos)
  return route
}

async function handleReasonChanged(...args: unknown[]) {
  const optionVal = args[0] as string
  const option: Option = reasonOptions.value.find((option) => option.value === optionVal)!
  if (option.value === 'newCar') {
    const changeVehicleRoute = getRoute() + '/fahrzeug-wechseln'
    actionText.value = t('termination.action.newCar', [changeVehicleRoute]) as string
  } else if (option.value === 'newAddress') {
    let changeAddressRoute = globalThis.ENV_CONFIG?.BASE_URL
    const changeAddressPage =
      globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_DATA_CHANGE_ADDRESS_PAGE
    if (changeAddressRoute.endsWith('/') && changeAddressPage.startsWith('/')) {
      // one '/' too much when concatenated
      changeAddressRoute += changeAddressPage.substring(1)
    } else {
      changeAddressRoute += changeAddressPage
    }
    actionText.value = t('termination.action.newAddress', [changeAddressRoute]) as string
  } else if (option.value === 'serviceScope') {
    const changeVersicherungsumfangRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.COVERAGE_CHANGE ?? '')
    )
    const changeFahrleistungRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.MAX_MILEAGE_CHANGE ?? '')
    )
    const changeFahrerkreisRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.CIRCLE_OF_DRIVERS_CHANGE ?? '')
    )
    actionText.value = t('termination.action.serviceScope', [
      changeVersicherungsumfangRoute,
      changeFahrleistungRoute,
      changeFahrerkreisRoute
    ]) as string
  } else if (option.value === 'contributionAmount') {
    const changeVersicherungsumfangRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.COVERAGE_CHANGE ?? '')
    )
    const changeFahrleistungRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.MAX_MILEAGE_CHANGE ?? '')
    )
    const changeFahrerkreisRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.CIRCLE_OF_DRIVERS_CHANGE ?? '')
    )
    actionText.value = t('termination.action.contributionAmount', [
      changeVersicherungsumfangRoute,
      changeFahrleistungRoute,
      changeFahrerkreisRoute
    ]) as string
  } else if (option.value === 'changeInsurance') {
    const changeVersicherungsumfangRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.COVERAGE_CHANGE ?? '')
    )
    const changeFahrleistungRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.MAX_MILEAGE_CHANGE ?? '')
    )
    const changeFahrerkreisRoute = removeDuplicateSlashesInString(
      globalThis.ENV_CONFIG?.BASE_URL +
        (globalThis.ENV_CONFIG?.SERVICE_LINKS.CIRCLE_OF_DRIVERS_CHANGE ?? '')
    )
    actionText.value = t('termination.action.changeInsurance', [
      changeVersicherungsumfangRoute,
      changeFahrleistungRoute,
      changeFahrerkreisRoute
    ]) as string
  } else {
    actionText.value = t('termination.action.' + option.value) as string
  }

  // get ppId from token
  partnerProxyId.value = getPartnerProxyId()

  writeLog(option)
}

function sanitize(input: string): string {
  return input.replace(/[\n\r]/g, '') // Entfernt neue Zeilen, um Log Injection zu verhindern
}

function writeLog(option: Option) {
  if (!partnerProxyId.value) return

  const requestBody = {
    value: sanitize(option.value),
    description: sanitize(option.label)
  }

  logTerminationReason(partnerProxyId.value, props.policyId, requestBody).catch(
    (error) => {
      console.error('Error during fetch:', JSON.stringify(error.message))
    }
  )
}
</script>

<style lang="scss" scoped>
.card-outer.dropdown-spacing > .card {
  margin-bottom: 90px;
}

#select-div {
  max-width: 600px;
}

.padding-all-0 {
  padding: 0 !important;
}

.is-styled .selectmenue .vs__selected {
  height: auto;
}

input.vs__search {
  display: none;
}

.alert > .text {
  font-family: var(--f-primary-base), serif !important;

  b {
    font-family: var(--f-primary-bold), serif;
  }
}
.is-styled .mt-47 {
  margin-top: 47px !important;
}
.is-styled .card-row__col > h2 {
  margin-bottom: 20px;
}
</style>
